/**
 * SmithImportExport (Iter168) — inline toolbar block used on both the
 * Smith Dashboard and the Prospects list. Handles:
 *
 *   • Export dropdown → XLSX · CSV · PDF · DOCX (respects current filters)
 *   • Import button   → modal with drag&drop XLSX/CSV upload
 *
 * The parent passes `filters` as a plain object; we serialise it directly
 * to the export URL so what the user sees on screen is exactly what lands
 * in the file.
 */
import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Download, Upload, FileSpreadsheet, FileText, FileType2, File as FileIcon,
  Loader2, X, ChevronDown, CheckCircle2, AlertTriangle, HelpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPORT_FORMATS = [
  { fmt: "xlsx", label: "Excel (.xlsx)", icon: FileSpreadsheet, color: "#10B981" },
  { fmt: "csv",  label: "CSV",           icon: FileText,        color: "#3B82F6" },
  { fmt: "pdf",  label: "PDF (relatório)", icon: FileIcon,      color: "#DC2626" },
  { fmt: "docx", label: "Word (.docx)",  icon: FileType2,       color: "#8B5CF6" },
];

const cleanFilters = (obj) => {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === 0) return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(",");
    } else {
      out[k] = String(v);
    }
  });
  return out;
};

export default function SmithImportExport({ filters, onImported, testidPrefix = "smith-io", compact = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const cleaned = useMemo(() => cleanFilters(filters), [filters]);
  const filterCount = Object.keys(cleaned).length;

  const doExport = async (fmt) => {
    setMenuOpen(false);
    setDownloading(fmt);
    try {
      const params = new URLSearchParams({ ...cleaned, fmt });
      const url = `${API}/smith/prospects/export?${params.toString()}`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "");
      link.download = `smith_prospects_${stamp}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Export .${fmt} pronto`, { description: filterCount ? `Filtros aplicados: ${filterCount}` : undefined });
    } catch (e) {
      toast.error(`Falha no export .${fmt}`, { description: e?.response?.data?.detail || e.message });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <>
      <div className={"inline-flex items-center gap-1.5 " + (compact ? "" : "")}
           data-testid={`${testidPrefix}-toolbar`}>
        {/* Export dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            disabled={!!downloading}
            data-testid={`${testidPrefix}-export-btn`}
            className="h-9 px-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-[12.5px] font-bold text-emerald-300 hover:border-emerald-400 hover:text-emerald-200 inline-flex items-center gap-1.5 transition-colors disabled:opacity-60"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span>Export</span>
            {filterCount > 0 && (
              <span className="text-[9.5px] font-bold px-1 py-0.5 rounded bg-emerald-500/25">
                {filterCount} filtro{filterCount > 1 ? "s" : ""}
              </span>
            )}
            <ChevronDown className="w-3 h-3 opacity-70" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div
                className="absolute right-0 mt-1 z-50 w-56 rounded-lg border shadow-xl overflow-hidden"
                data-testid={`${testidPrefix}-export-menu`}
                style={{ background: "rgba(11,17,32,0.98)", borderColor: "rgba(255,255,255,0.15)" }}
              >
                {EXPORT_FORMATS.map(({ fmt, label, icon: Icon, color }) => (
                  <button
                    key={fmt}
                    type="button"
                    onClick={() => doExport(fmt)}
                    data-testid={`${testidPrefix}-export-${fmt}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[12.5px] text-slate-200 hover:bg-white/5 text-left"
                  >
                    <Icon className="w-4 h-4 shrink-0" style={{ color }} />
                    <span className="flex-1">{label}</span>
                  </button>
                ))}
                <div className="border-t border-white/10 px-3 py-2 text-[10.5px] text-slate-400 bg-black/30">
                  {filterCount > 0
                    ? `Aplicará ${filterCount} filtro${filterCount > 1 ? "s" : ""} atual${filterCount > 1 ? "is" : ""}`
                    : "Sem filtros — exporta todo o universo"}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Import */}
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          data-testid={`${testidPrefix}-import-btn`}
          className="h-9 px-3 rounded-lg border border-[#DC2626]/40 bg-[#DC2626]/10 text-[12.5px] font-bold text-[#FCA5A5] hover:border-[#DC2626] hover:text-white inline-flex items-center gap-1.5 transition-colors"
        >
          <Upload className="w-3.5 h-3.5" />
          <span>Import</span>
        </button>
      </div>

      {modalOpen && (
        <ImportModal
          onClose={() => setModalOpen(false)}
          onDone={onImported}
          testidPrefix={testidPrefix}
        />
      )}
    </>
  );
}

function ImportModal({ onClose, onDone, testidPrefix }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onFilePick = (f) => {
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".csv")) {
      toast.error("Formato inválido — envie .xlsx ou .csv");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    onFilePick(e.dataTransfer?.files?.[0]);
  };

  const doUpload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/smith/prospects/import?dry_run=${dryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (!dryRun) {
        toast.success(`Import concluído · ${r.data.inserted} novos · ${r.data.updated} atualizados`);
        onDone?.(r.data);
      } else {
        toast.info("Preview gerado — revise antes de confirmar");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no import");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/smith/prospects/import-template`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data]));
      link.download = "smith_import_template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch {
      toast.error("Falha ao baixar template");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
         data-testid={`${testidPrefix}-import-modal`}
         onClick={onClose}>
      <div className="relative w-full max-w-lg rounded-2xl border p-5 space-y-4"
           style={{ background: "#0B1120", borderColor: "rgba(220,38,38,0.30)" }}
           onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose}
                data-testid={`${testidPrefix}-import-close`}
                className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-white/5 text-slate-400 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>

        <div>
          <h3 className="text-[15px] font-extrabold text-white">Importar prospects</h3>
          <p className="text-[11.5px] text-slate-400 mt-0.5">
            Aceita .xlsx ou .csv. Duplicatas por <span className="font-mono">company + address</span> são atualizadas em vez de recriadas.
          </p>
        </div>

        <button type="button" onClick={downloadTemplate}
                data-testid={`${testidPrefix}-import-template`}
                className="text-[11.5px] text-emerald-400 hover:text-emerald-300 underline decoration-dotted inline-flex items-center gap-1">
          <HelpCircle className="w-3.5 h-3.5" />
          Baixar template com exemplo
        </button>

        {/* Drop zone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testidPrefix}-import-dropzone`}
          className="rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all hover:border-[#DC2626]/60 hover:bg-[#DC2626]/5"
          style={{ borderColor: file ? "#10B981" : "rgba(255,255,255,0.15)" }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            hidden
            onChange={(e) => onFilePick(e.target.files?.[0])}
            data-testid={`${testidPrefix}-import-file-input`}
          />
          {file ? (
            <div className="text-emerald-300 text-[13px] font-bold flex items-center justify-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              {file.name}
              <span className="text-[11px] text-slate-400 font-normal">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-500 mx-auto mb-2" />
              <div className="text-[13px] text-slate-200 font-bold">Arraste um arquivo aqui</div>
              <div className="text-[11px] text-slate-400 mt-0.5">ou clique para selecionar (.xlsx, .csv · máx 5MB)</div>
            </>
          )}
        </div>

        {/* Dry-run toggle */}
        <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            data-testid={`${testidPrefix}-import-dryrun`}
            className="accent-[#DC2626]"
          />
          <span>Preview antes de gravar (recomendado)</span>
        </label>

        {/* Result */}
        {result && (
          <div className="rounded-lg border p-3 text-[11.5px]"
               data-testid={`${testidPrefix}-import-result`}
               style={{
                 borderColor: result.skipped ? "#F59E0B55" : "#10B98155",
                 background: result.skipped ? "#F59E0B10" : "#10B98110",
               }}>
            <div className="flex items-center gap-2 font-bold text-white mb-1">
              {result.skipped ? <AlertTriangle className="w-4 h-4 text-amber-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              {result.dry_run ? "Preview" : "Concluído"} · {result.rows_read} linhas lidas
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Novos" value={result.inserted} color="#10B981" />
              <Stat label="Atualizados" value={result.updated} color="#3B82F6" />
              <Stat label="Ignorados" value={result.skipped} color="#F59E0B" />
            </div>
            {result.unrecognised_columns?.length > 0 && (
              <div className="mt-2 text-slate-400">
                Colunas ignoradas: <span className="font-mono text-slate-200">{result.unrecognised_columns.slice(0, 8).join(", ")}</span>
              </div>
            )}
            {result.errors?.length > 0 && (
              <details className="mt-2 text-slate-400">
                <summary className="cursor-pointer hover:text-white">{result.errors.length} avisos</summary>
                <ul className="mt-1 space-y-0.5 pl-2 max-h-32 overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <li key={i} className="text-slate-500">• {err}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testidPrefix}-import-cancel`}
            className="h-9 px-4 rounded-lg border border-white/15 text-[12.5px] font-bold text-slate-300 hover:bg-white/5"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={doUpload}
            disabled={!file || busy}
            data-testid={`${testidPrefix}-import-submit`}
            className="ml-auto h-9 px-4 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "#DC2626" }}
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {dryRun ? "Gerar Preview" : "Confirmar Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-md py-1.5 border" style={{ borderColor: `${color}30`, background: `${color}0C` }}>
      <div className="text-[16px] font-extrabold tabular-nums" style={{ color }}>{value ?? 0}</div>
      <div className="text-[9.5px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
    </div>
  );
}
